import { getUserApplications, getUserRoleResources } from '@/api/userApi';
import config from '@/config/user.env';
import { USER_INFO, CUR_ROLE } from '@/config/const';
import cookies from 'js-cookie';
import { clearMicroAppPrefetchState } from '@/utils/prefetchMicroAppsByAuthority';
import { normalizeUserCode } from '@/utils/userCodeHelper';

/** 统一角色预取缓存 key，避免 bootstrap(apmUser) 与 initSystem(userInfo.userCode) 大小写不一致导致 miss */
const buildRolePrefetchKey = (userCode: string, roleCode: string) => {
  const normalizedUserCode = normalizeUserCode((userCode ?? '').trim());
  const normalizedRoleCode = (roleCode ?? '').trim();
  return {
    key: `${normalizedUserCode}::${normalizedRoleCode}`,
    normalizedUserCode,
    roleCode: normalizedRoleCode,
  };
};

const isValidRoleResources = (res: any) => res && +res.code === 0;

const isValidApplications = (res: any) =>
  res && +res.code === 0 && Array.isArray(res.data) && res.data.length > 0;

let rolePrefetchKey: string | null = null;
let rolePrefetchPromise: Promise<any> | null = null;
let roleCachedResult: any = null;

let appPrefetchUserCode: string | null = null;
let appPrefetchPromise: Promise<any> | null = null;
let appCachedResult: any = null;

/**
 * 预取角色资源（选角色时后台发起；若 setTenant 未执行可能无效，initSystem 会 fallback 重新请求）
 * 子应用 preFetch 仅在登录页触发，不在此处联动（避免与即将挂载并发）。
 */
export const prefetchUserRoleResources = (userCode: string, roleCode: string) => {
  const { key, normalizedUserCode, roleCode: roleCodes } = buildRolePrefetchKey(userCode, roleCode);
  if (!normalizedUserCode || !roleCodes) return null;
  if (rolePrefetchKey === key && (roleCachedResult || rolePrefetchPromise)) {
    return rolePrefetchPromise;
  }
  rolePrefetchKey = key;
  roleCachedResult = null;
  rolePrefetchPromise = getUserRoleResources({
    userCode: normalizedUserCode,
    applicationCode: config.applicationCode,
    tenantCode: 'annto',
    roleCodes,
  })
    .then((res) => {
      // 仅当回调完成时 key 未变，才写入缓存，避免快速切换角色时旧请求迟到污染
      if (rolePrefetchKey === key && isValidRoleResources(res)) {
        roleCachedResult = res;
      }
      return res;
    })
    .catch(() => {
      if (rolePrefetchKey === key) {
        rolePrefetchKey = null;
        rolePrefetchPromise = null;
      }
      return null;
    });
  return rolePrefetchPromise;
};

/** 消费预取的角色资源，仅 code===0 且 key 匹配时命中 */
export const getPrefetchedRoleResources = async (userCode: string, roleCode: string) => {
  const { key, normalizedUserCode, roleCode: roleCodes } = buildRolePrefetchKey(userCode, roleCode);
  if (!normalizedUserCode || !roleCodes) {
    return null;
  }
  if (rolePrefetchKey !== key) {
    return null;
  }
  if (roleCachedResult) {
    const result = roleCachedResult;
    clearRoleResourcesPrefetch();
    return result;
  }
  if (rolePrefetchPromise) {
    const result = await rolePrefetchPromise;
    // 等待期间可能已切换角色，再次校验 key
    if (rolePrefetchKey !== key) {
      return null;
    }
    clearRoleResourcesPrefetch();
    return isValidRoleResources(result) ? result : null;
  }
  return null;
};

/** 预取应用列表 */
export const prefetchUserApplications = (userCode: string) => {
  if (!userCode) return null;
  if (appPrefetchUserCode === userCode && (appCachedResult || appPrefetchPromise)) {
    return appPrefetchPromise;
  }
  appPrefetchUserCode = userCode;
  appCachedResult = null;
  appPrefetchPromise = getUserApplications({ userCode, tenantCode: 'annto' })
    .then((res) => {
      if (appPrefetchUserCode === userCode && isValidApplications(res)) {
        appCachedResult = res;
      }
      return res;
    })
    .catch(() => {
      if (appPrefetchUserCode === userCode) {
        clearApplicationsPrefetch();
      }
      return null;
    });
  return appPrefetchPromise;
};

/** 消费预取的应用列表，仅有效非空列表时命中 */
export const getPrefetchedApplications = async (userCode: string) => {
  if (appPrefetchUserCode !== userCode) {
    return null;
  }
  if (appCachedResult) {
    const result = appCachedResult;
    clearApplicationsPrefetch();
    return result;
  }
  if (appPrefetchPromise) {
    const result = await appPrefetchPromise;
    if (appPrefetchUserCode !== userCode) {
      return null;
    }
    clearApplicationsPrefetch();
    return isValidApplications(result) ? result : null;
  }
  return null;
};

export const clearRoleResourcesPrefetch = () => {
  rolePrefetchKey = null;
  rolePrefetchPromise = null;
  roleCachedResult = null;
};

const clearApplicationsPrefetch = () => {
  appPrefetchUserCode = null;
  appPrefetchPromise = null;
  appCachedResult = null;
};

export const clearAllPrefetch = () => {
  clearRoleResourcesPrefetch();
  clearApplicationsPrefetch();
  clearMicroAppPrefetchState();
};

/**
 * F5 / 已登录启动：尽早预取角色资源接口。
 * 不在此 preFetch 首页子应用——强刷新时预加载与挂载间隔过短，预加载无作用。
 */
export const bootstrapLoggedInRolePrefetch = () => {
  if (!localStorage.getItem(USER_INFO)) {
    return;
  }
  const curRole = localStorage.getItem(CUR_ROLE);
  const userCode = cookies.get('apmUser');
  if (userCode && curRole) {
    prefetchUserRoleResources(userCode, curRole);
  }
};
